'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { Icon } from '@/app/components/Icon'
import { KiteLogo } from '@/app/components/KiteLogo'
import { LanguageSelector } from '@/app/components/LanguageSelector'
import { Modal } from '@/app/components/Modal'
import { Navigation } from '@/app/components/Navigation'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { getLocaleFromPathname } from '@/lib/i18n'
import { NavQueryResult } from '@/sanity.types'

interface HeaderProps {
  className?: string
  nav: NavQueryResult[number] | null
  navCta: NavQueryResult[number] | null
}

export const Header = function ({
  className,
  nav: initialNav,
  navCta: initialNavCta,
}: HeaderProps) {
  const [menuVisible, setMenuVisible] = useState(false)
  const [nav, setNav] = useState(initialNav)
  const [navCta, setNavCta] = useState(initialNavCta)
  const [isScrolled, setIsScrolled] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    setNav(initialNav)
    setNavCta(initialNavCta)
  }, [initialNav, initialNavCta])

  useEffect(() => {
    const locale = getLocaleFromPathname(pathname)

    fetch(`/api/navigations?locale=${locale}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        return res.json()
      })
      .then((data) => {
        setNav(data.header)
        setNavCta(data.header_cta)
      })
      .catch((err) => console.error('Failed to fetch navigations:', err))
  }, [pathname])

  // Handle scroll position for lg+ breakpoint (sticky only when scrolled 24px+)
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 24)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu when pathname changes
  useEffect(() => {
    setMenuVisible(false)
  }, [pathname])

  return (
    <>
      <header
        className={cn(
          'z-50 h-[42px] lg:h-[66px] bg-white flex items-center sticky top-0 mx-0 lg:mt-2xl shadow-nav lg:shadow-none lg:transition-all lg:duration-300',
          menuVisible && 'shadow-none',
          isScrolled
            ? 'lg:mx-0 lg:rounded-none lg:shadow-nav' // lg+: full width when scrolled
            : 'lg:mx-5xl lg:rounded-xl', // lg+: default styling (with margin and rounded corners)
          className,
        )}
      >
        <div
          className={cn(
            'w-full py-md px-lg lg:pl-2xl lg:pr-md lg:transition-all lg:duration-300',
            isScrolled ? 'lg:mx-5xl' : 'lg-mx-auto',
          )}
        >
          <div className="flex items-center justify-between gap-5">
            <Link className="flex items-center gap-2" href="/" aria-label="Kite Mobile">
              <KiteLogo className="w-[59px] h-[18px] lg:w-[80px] lg:h-[24px]" />
            </Link>
            <button
              type="button"
              onClick={() => setMenuVisible(!menuVisible)}
              aria-label="Toggle menu"
              aria-expanded={menuVisible}
              className="lg:hidden cursor-pointer"
            >
              <Icon
                name={menuVisible ? 'faClose' : 'faBars'}
                provider="fontawesome"
                style="solid"
                size={16}
                className="text-primary-600"
              />
            </button>
            <div className="hidden lg:flex lg:flex-row lg:gap-5xl items-center">
              {nav && (
                <Navigation
                  nav={nav}
                  tracking={{ location: TRACKING_LOCATIONS.HEADER }}
                  classNames={{ root: 'flex items-center', list: 'flex flex-row gap-5xl' }}
                />
              )}
              <LanguageSelector />
              {navCta && (
                <Navigation
                  nav={navCta}
                  tracking={{ location: TRACKING_LOCATIONS.HEADER }}
                  classNames={{ root: 'flex items-center', list: 'flex flex-row gap-5xl' }}
                />
              )}
            </div>
          </div>
        </div>
        {/* Mobile menu overlay */}
        <Modal isOpen={menuVisible} onClose={() => setMenuVisible(false)}>
          <div className="px-lg py-5xl flex flex-col gap-xl">
            {nav && (
              <Navigation
                nav={nav}
                tracking={{ location: TRACKING_LOCATIONS.MOBILE_MENU }}
                classNames={{ root: 'flex flex-col', list: 'flex flex-col gap-xl items-center' }}
              />
            )}
            {navCta && (
              <Navigation
                nav={navCta}
                tracking={{ location: TRACKING_LOCATIONS.MOBILE_MENU }}
                classNames={{
                  root: 'flex flex-col',
                  list: 'flex flex-col gap-xl items-center',
                }}
              />
            )}
          </div>
          <div className="mt-auto px-lg py-md">
            <LanguageSelector />
          </div>
        </Modal>
      </header>
    </>
  )
}
